import React from 'react';
import { Box, Card, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CRM_COLORS, CRM_RADIUS } from '../../theme/crmColors';
import { BREAKPOINTS } from '../../theme/breakpoints';

const useStyles = makeStyles(() => ({
    card: {
        borderRadius: CRM_RADIUS.card,
        boxShadow: CRM_COLORS.shadow,
        border: `1px solid ${CRM_COLORS.border}`,
        backgroundColor: CRM_COLORS.surface,
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16,
        padding: '16px 20px',
        borderBottom: `1px solid ${CRM_COLORS.borderSoft}`,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            padding: '12px 16px',
        },
    },
    icon: {
        width: 36,
        height: 36,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: CRM_COLORS.primary,
        backgroundColor: CRM_COLORS.status.info.background,
        borderRadius: CRM_RADIUS.control,
        flexShrink: 0,
    },
    titleBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 0,
    },
    title: {
        fontSize: '1.0625rem',
        fontWeight: 600,
        color: CRM_COLORS.secondary,
        lineHeight: 1.3,
    },
    subtitle: {
        fontSize: '0.8125rem',
        color: CRM_COLORS.text.secondary,
    },
    actions: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            marginLeft: 0,
        },
    },
    content: {
        padding: '20px 24px 24px',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            padding: '12px 12px 16px',
        },
    },
}));

interface DashboardCardProps {
    title: string;
    subtitle?: string;
    icon?: React.ReactNode;
    /** Controls on the right of the header */
    actions?: React.ReactNode;
    children: React.ReactNode;
}

const DashboardCard: React.FC<DashboardCardProps> = ({ title, subtitle, icon, actions, children }) => {
    const classes = useStyles();

    return (
        <Card className={classes.card} elevation={0}>
            <Box className={classes.header}>
                {icon && <Box className={classes.icon}>{icon}</Box>}
                <Box className={classes.titleBlock}>
                    <Typography className={classes.title}>{title}</Typography>
                    {subtitle && <Typography className={classes.subtitle}>{subtitle}</Typography>}
                </Box>
                {actions && <Box className={classes.actions}>{actions}</Box>}
            </Box>
            <Box className={classes.content}>{children}</Box>
        </Card>
    );
};

export default DashboardCard;
